//Library Imports
import { useEffect } from "react";
import { Popover } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useDrag, DragSourceMonitor } from "react-dnd";
//Local Imports
import AddMore from "./AddMore";
import DragIcon from "assets/img/icons/DragIcon.svg";
import { ItemTypes } from "modules/Common/Components/ItemTypes";
import PopoverContent from "./PopoverContent";
import { ControlNameEnum, IListData, WFStatus, WFStatusEnum } from "../interfaces";
import { RootState } from "store";
//Style Imports
import styled from "styled-components";
import { Link } from "react-router-dom";
import useQuery from "hooks/useQuery";
//Icons Imports
import AddToListIcon from "../../../assets/img/icons/addToListIcon.svg";
import DelayIcon from "../../../assets/img/icons/delayIcon.svg";
import EmailIcon from "../../../assets/img/icons/emailIcon.svg";
import StopIcon from "../../../assets/img/icons/stopIcon.svg";
import { DESIGN } from "constants/actionTypes";

type Props = {
  controlName: string;
  controlTitle: string;
  nodeTitle?: string;
  id: string;
  uperline?: boolean;
  downline?: boolean;
  email?: boolean;
  wait?: boolean;
  list?: boolean;
  end?: boolean;
  disabled: boolean;
  data: object;
  hasChildren: boolean;
  scaling: string;
};

const ActionNode = (props: Props) => {
  const {
    controlName,
    controlTitle,
    nodeTitle,
    id,
    uperline,
    downline,
    email,
    wait,
    list,
    disabled,
    end,
    data,
    hasChildren,
    scaling,
  } = props;
  // Redux state
  const workflowStatus: WFStatus = useSelector((state: RootState) => state.automation.workflow?.detail?.status);
  const triggerEmailSent: boolean = useSelector((state: RootState) => state.automation.workflow?.detail?.triggerEmailSent);

  // Local variables
  const query = useQuery();
  const workflowId = query.get("workflow_id");
  const isStatusNotDraft = workflowStatus && workflowStatus !== WFStatusEnum.DRAFT;
  const isStatusNotActive = workflowStatus && workflowStatus !== WFStatusEnum.ACTIVE;
  const disptach = useDispatch();

  const redirectionLinks = {
    'sent': `/campaigns/editionedit/${data?.versionEmailType === 0 ? '' : 'design/'}${data?.campaignId ? `?campaign_id=${data?.campaignId}&` : "?"
      }email_id=${data?.emailId}&workflow_id=${workflowId}`,
    'default': `/campaigns/editionedit/design/${data?.campaignId ? `?campaign_id=${data?.campaignId}&` : "?"
      }email_id=${data?.emailId}&workflow_id=${workflowId}`
  }

  const emailRedirectionLink = redirectionLinks[triggerEmailSent ? 'sent' : 'default'];

  const [{ isDragging }, dragRef, dragPreview] = useDrag(
    () => ({
      type: ItemTypes.CARD,
      item: { nodeTitle, title: controlTitle, controlName, ...data, nodeId: id },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: (monitor: DragSourceMonitor) => {
        // status should not equal to empty string and also should not have the "draft" status
        if (isStatusNotDraft) return false;
        return true;
      },
    }),
    [id, controlName, nodeTitle]
  ); // these dependencies are important.
  // otherwise if elements are dragged then after some time it get memoized and then same element

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    // <Wrapper className={list ? "list-type" : end ? "end-type" : null}>
    <Wrapper
      className={`${list ? "list-type" : end ? "end-type" : null} ${scaling === "scaled" ? "zoom-out" : "zoom-in"
        }`}
      status={workflowStatus}
      controlName={controlName}
    >
      {uperline ? <div className="uperline"></div> : null}
      <Popover
        placement="topRight"
        content={isStatusNotActive ? <PopoverContent id={id} controlName={controlName} canCopy={!end} canEdit={!end} /> : null}
        getPopupContainer={() => document.querySelectorAll(".workflow-container")[0] as HTMLElement}
        // trigger="click"
        overlayClassName="workflow-popover workflow-popover-right"
      >
        <div
          className={`action-wrapper ${disabled ? "disabled" : ""} ${scaling === "scaled" ? "zoom-out" : "zoom-in"
            }`}
          ref={dragRef}
        >
          {/* disabled toggle class for disable on drag */}
          {email && <img src={EmailIcon} alt="" className="icon" />}
          {end && <img src={StopIcon} alt="" className="icon" />}
          {wait ? <img src={DelayIcon} alt="" className="icon" style={{ marginTop: "2px" }} /> : null}
          {list && <img src={AddToListIcon} alt="" className="icon" />}
          <span className={`cover-action ${list || wait ? "icon-width" : ""}`}>
            <span className="action-type">{wait ? controlTitle?.split("days")[0]?.trim() : controlTitle}</span>
            {nodeTitle && (
              <span className="action-label">
                {controlName === ControlNameEnum.ADD_LIST ? (
                  <>
                    {": "}
                    {data?.list?.map((li: IListData) => (
                      <>
                        <Link
                          className="list-name-styles"
                          to={`/audiences/lists/view?list_id=${li.list_id}&workflow_id=${workflowId}`}
                        >{`${li.list_name}`}</Link>
                        {`${data?.list[data?.list?.length - 1] === li ? "" : " ,"}`}
                      </>
                    ))}
                  </>
                ) : controlName === ControlNameEnum.SEND_EMAIL && data?.emailType !== "share profile" ? (
                  <>
                    {": "}
                    {/* in order to clear the store for design we need to reset it */}
                    <Link className="list-name-styles" onClick={() => { disptach({ type: DESIGN.RESET_DESIGN }); }} to={emailRedirectionLink}>
                      {data?.emailName}
                    </Link>
                  </>
                ) : (
                  nodeTitle
                )}
              </span>
            )}
          </span>
          {!isStatusNotDraft && (
            <span className="drag-handle">
              <img src={DragIcon} alt="" />
            </span>
          )}
        </div>
      </Popover>
      {downline && (
        <>
          <div className="downline"></div>
          <AddMore id={id} disabled={disabled} hasChildren={hasChildren} />
        </>
      )}
    </Wrapper>
  );
};

export default ActionNode;

type WrapperProps = {
  status?: any;
  controlName?: string;
};

const Wrapper = styled.div<WrapperProps>`
  display: block;
  text-align: center;
  clear: both;
  margin: 0 30px;
  .action-wrapper {
    background-color: #ffffff;
    border-radius: 8px;
    padding: 9px 14px;
    border: 1px solid ${(props) => (props.status === WFStatusEnum.INACTIVE && props.controlName === ControlNameEnum.SEND_EMAIL ? "#1ec252" : "#7e7e7e")};
    display: inline-block;
    margin: 0 auto;
    /* white-space: nowrap; */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      border-radius: 6px;
      padding: 6px 10px;
    }
    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }
    &.zoom-out {
      border: 2px solid #7e7e7e;
    }
    &:hover .drag-handle {
      opacity: 1;
    }
    .icon {
      width: 20px;
      height: 20px;
      vertical-align: top;
      margin-right: 8px;
      float: left;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 16px;
        height: 16px;
      }
    }
    .drag-handle {
      position: absolute;
      right: -36px;
      top: 50%;
      transform: translateY(-50%);
      cursor: grab;
      transition: 0.5s;
      opacity: 0;
      img {
        width: 16px;
        height: 16px;
      }
    }
    span {
      color: #001823;
      font-size: 16px;
      font-family: "Roboto", sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
      &.cover-action {
        display: inline-block;
        max-width: 320px;
        white-space: normal;
        text-align: left;
        float: left;
        overflow: hidden;
        &.icon-width {
          /* width: calc(100% - 28px); */
        }
      }
      &.action-type {
        font-weight: 700;
        /* margin-right:5px; */
      }
      &.action-label {
        font-weight: 500;
        :hover {
          cursor: pointer;
        }
      }
    }
  }
  .list-name-styles {
    /* color: #1ec252; */
    text-decoration: underline;
    text-decoration-color: #1ec252;
    /* max-width: 200px;
    display: inline-flex; */
  }
  .downline {
    background-color: #979797;
    width: 2px;
    height: 25px;
    margin: -6px auto 0 auto;
  }
  .uperline {
    background-color: #979797;
    width: 2px;
    height: 30px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
    &:after {
      content: "";
      width: 8px;
      height: 8px;
      border-radius: 10px;
      background-color: #979797;
      display: inline-block;
      position: absolute;
      bottom: -3px;
      left: -3px;
    }
  }
  /* .add-more{
  width:27px;
  height:27px;
  margin: 0 auto;
  cursor: pointer;
  border-radius:50%;
  &.active{
    box-shadow: 0 0 20px 10px rgb(0 0 0 / 12%);
  }
} */
  &.list-type {
    .action-wrapper {
      border-color: #1ec252;
      background-color: #e6f5ea;
      padding: 12px 18px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 8px 12px;
      }
      span {
        color: #7e7e7e;
        font-size: 14px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 11px;
        }
      }
    }
    &.zoom-out {
      .action-wrapper {
        border: 2px solid #1ec252;
      }
    }
  }
  &.end-type {
    .action-wrapper {
      border-color: #e41010;
      background-color: #ffe7e7;
      padding: 12px 18px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 8px 12px;
      }
      span {
        color: #7e7e7e;
        font-size: 14px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 11px;
        }
      }
    }
    &.zoom-out {
      .action-wrapper {
        border: 2px solid #e41010;
      }
    }
  }
  /* .dropable-active{
  position:relative;
  margin:0 auto;
  display:inline-block;
  &:before{
    content:"";
    width: 130px;
    height:45px;
    top: -23px;
    left: 2px;
    transform: translateX(-50%);
    border-radius:8px;
    border:1px dashed #7E7E7E;
    background-color: #E6F5EA;
    position:absolute;
    z-index:2;
  }
  .add-more{
    opacity:0;
  }
} */
  .nodeDisable{
    opacity: 0.5;
    pointer-events: none;
  }
  .fade{
    opacity:0.5;
  }
`;
